import React, { useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useQuery } from 'react-query'
import { ayudaApi } from '../../../services/api'
import Table from '../../common/Table'

export const AuditTrailModal = ({ setShowModal }) => {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  const {
    data: rawData,
    isLoading,
    isFetching,
  } = useQuery(
    ['ayuda-audit-logs', page, perPage],
    async () => {
      const res = await ayudaApi.getAyudaLogs({ page, per_page: perPage })
      const payload = res?.data?.data || {}
      const logs = payload.logs || []

      const mappedLogs = logs.map((log) => {
        let oldValues = {}
        let newValues = {}
        try {
          oldValues = JSON.parse(log.old_values)
        } catch {}
        try {
          newValues = JSON.parse(log.new_values)
        } catch {}

        return {
          ...log,
          user_name: log.user_name || `User ID: ${log.user_id}`,
          details: `Old: ${JSON.stringify(
            oldValues,
            null,
            2
          )}\nNew: ${JSON.stringify(newValues, null, 2)}`,
        }
      })

      return {
        logs: mappedLogs,
        meta: payload.meta || {},
      }
    },
    { keepPreviousData: true }
  )

  const handlePageChange = (newPage, newPerPage) => {
    if (newPerPage && newPerPage !== perPage) {
      setPerPage(newPerPage)
      setPage(1)
    } else {
      setPage(newPage)
    }
  }

  const columns = [
    {
      key: 'created_at',
      label: 'Date',
      render: (value) => {
        const date = new Date(value)
        const formatted =
          date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }) +
          ' ' +
          date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          })
        return <span className='font-sans text-sm'>{formatted}</span>
      },
    },
    {
      key: 'user_name',
      label: 'User',
      render: (_, row) => {
        const userName = row.user_name || ''
        const isOnlyId = userName.startsWith('User ID:')
        const parts = userName.split('ID:')
        const name = parts[0]?.trim()
        const id = parts[1]?.trim()

        return (
          <div className='flex flex-col font-sans text-sm'>
            {isOnlyId ? (
              <span>{userName}</span>
            ) : (
              <>
                <span>{name || 'Unknown'}</span>
                {id && <span className='text-gray-500'>ID: {id}</span>}
              </>
            )}
          </div>
        )
      },
    },
    {
      key: 'txid',
      label: 'Transaction Hash',
      render: (_, row) => (
        <a
          href={row.transaction_url}
          target='_blank'
          className='hover:underline hover:text-blue-400 hover:cursor-pointer'
        >
          <pre className='whitespace-pre-wrap break-words font-sans text-sm'>
            {row.txid || '-'}
          </pre>
        </a>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      render: (_, row) => {
        const action = row.action ? row.action.toUpperCase() : '-'
        const colorMap = {
          CREATED: 'bg-blue-100 text-blue-800',
          UPDATED: 'bg-green-100 text-green-800',
          DELETED: 'bg-red-100 text-red-800',
        }
        const badgeColor = colorMap[action] || 'bg-gray-100 text-gray-800'

        return (
          <span className={`px-2 py-1 rounded font-sans text-sm ${badgeColor}`}>
            {action}
          </span>
        )
      },
    },
    {
      key: 'details',
      label: 'Details',
      render: (_, row) => {
        const [expanded, setExpanded] = useState(false)
        const [overflow, setOverflow] = useState(false)
        const preRef = React.useRef(null)

        React.useEffect(() => {
          if (preRef.current) {
            // Detect if content overflows 2 lines
            setOverflow(
              preRef.current.scrollHeight > preRef.current.clientHeight
            )
          }
        }, [])

        return (
          <div className='min-w-[300px] max-w-[400px] font-sans text-sm'>
            <pre
              ref={preRef}
              className={`whitespace-pre-wrap break-words ${
                !expanded ? 'line-clamp-2' : ''
              } font-sans text-sm`}
            >
              {row.details}
            </pre>
            {overflow && (
              <button
                onClick={() => setExpanded(!expanded)}
                className='mt-1 text-blue-600 text-sm font-medium font-sans'
              >
                {expanded ? 'View Less' : 'View More'}
              </button>
            )}
          </div>
        )
      },
    },
  ]

  const auditLogs = rawData?.logs || []
  const auditMeta = rawData?.meta || {}

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
      <div className='bg-white w-full max-w-7xl mx-4 rounded-xl shadow-lg overflow-hidden'>
        {/* Header */}
        <div className='flex items-center justify-between px-6 py-4 border-b'>
          <h2 className='text-lg font-semibold'>Audit Trail Logs</h2>
          <button
            onClick={() => setShowModal(false)}
            className='text-gray-400 hover:text-gray-600'
          >
            <XMarkIcon className='w-6 h-6' />
          </button>
        </div>

        {/* Table */}
        {isLoading || isFetching ? (
          <div className='text-center text-gray-500 py-10'>Loading logs...</div>
        ) : auditLogs.length === 0 ? (
          <div className='text-center text-gray-500 py-10'>No logs found.</div>
        ) : (
          <div className='px-6 max-h-[70vh] overflow-y-auto'>
            <Table
              columns={columns}
              data={auditLogs}
              noContainer={true}
              onRowClick={() => {}}
              pagination={
                auditMeta.total > 0
                  ? {
                      currentPage: page,
                      lastPage: auditMeta.last_page || 1,
                      perPage,
                      total: auditMeta.total || 0,
                    }
                  : null
              }
              onPageChange={handlePageChange}
              paginationProps={{
                showSizeSelector: true,
                onPerPageChange: (value) => handlePageChange(1, value),
                isLoading: isFetching,
              }}
            />
          </div>
        )}

        {/* Footer */}
        <div className='flex justify-end px-6 py-4 border-t'>
          <button
            onClick={() => setShowModal(false)}
            className='px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300'
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
